import React, { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Box, MenuItem, Paper, Select, TextField, Tooltip, Typography,
} from '@mui/material'
import {
  Area, Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import SpeedIcon from '@mui/icons-material/Speed'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import StorefrontIcon from '@mui/icons-material/Storefront'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import PercentIcon from '@mui/icons-material/Percent'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { useGetPocIndividualQuery } from '../../redux/growthosPocAnalyticsApiSlice'
import {
  GX_BORDER, GX_INK_500, GX_INK_700, GX_INK_900, GX_CARD_SX, GX_PAGE_SX, GX_COLORS, GX_NEGATIVE, GX_POSITIVE,
  GxKpiCard, GxPill,
} from '../growthos-shared/GrowthOsUi'
import { GxPagedTable } from '../growthos-shared/GxPagedTable'
import {
  comparison, compactCurrency, currency, integer, monthLabel, percentage, shortDate, GrowthPct,
} from '../growthos-shared/gxFormat'
import { PERIODS, ScopeTag } from './pocFormat'

const INK_400 = '#94a3b8'
const C = { shipments: '#0D9488', revenue: '#7C3AED', activeBrands: '#06B6D4', totalBrands: '#D97706' }
const axisTick = { fontSize: 11, fill: GX_INK_500 }

function Kpi({ definition, ...props }) {
  return (
    <Tooltip title={definition || ''} placement="top">
      <Box sx={{ flex: '1 1 220px', display: 'flex' }}><GxKpiCard {...props} /></Box>
    </Tooltip>
  )
}

function TrendTooltip({ active, payload, label, rows }) {
  if (!active || !payload || !payload.length) return null
  const byKey = {}
  payload.forEach((p) => { byKey[p.dataKey] = p.value })
  return (
    <Box sx={{ border: `1px solid ${GX_BORDER}`, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.95)', px: 1.5, py: 1, fontSize: 12, boxShadow: '0 8px 24px rgba(15,23,42,0.12)' }}>
      <Typography sx={{ fontWeight: 600, fontSize: 12, color: GX_INK_700, mb: 0.5 }}>{monthLabel(label)}</Typography>
      {rows.map((r) => (
        <Box key={r.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, py: 0.25 }}>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: GX_INK_500 }}>
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.color }} /> {r.label}
          </Box>
          <Box component="span" sx={{ fontWeight: 600, color: GX_INK_900 }}>{r.fmt(byKey[r.key] || 0)}</Box>
        </Box>
      ))}
    </Box>
  )
}

function ChartCard({ title, subtitle, empty, children }) {
  return (
    <Paper variant="outlined" sx={{ ...GX_CARD_SX, p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>{title}</Box>
      <Typography sx={{ fontSize: 12, color: INK_400, mb: 1.5 }}>{subtitle}</Typography>
      {empty ? (
        <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: 13, color: INK_400 }}>No shipment data in this period.</Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
        </Box>
      )}
    </Paper>
  )
}

function Section({ icon, title, subtitle, right, children, padded = true }) {
  return (
    <Paper variant="outlined" sx={{ ...GX_CARD_SX, mb: 3, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 2.5, pt: 2.5, pb: 1 }}>
        {icon}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 12, color: INK_400 }}>{subtitle}</Typography>}
        </Box>
        {right}
      </Box>
      <Box sx={padded ? { px: 2.5, pb: 2.5 } : undefined}>{children}</Box>
    </Paper>
  )
}

function RiskTable({ rows }) {
  if (!rows.length) return <Typography sx={{ p: 2, textAlign: 'center', fontSize: 13, color: INK_400 }}>None in this period.</Typography>
  return (
    <Box sx={{ p: 1.5 }}>
      <GxPagedTable variant="poc"
        rows={rows}
        rowKey={(r) => r.brandId}
        columns={[
          { key: 'brand', header: 'Brand', title: 'Brand name (ProShip / Data → Brands).', sortValue: (r) => r.brandName, cell: (r) => <Typography component="span" sx={{ fontWeight: 600, fontSize: 12.5 }}>{r.brandName}</Typography> },
          { key: 'prevRev', header: 'Prev Revenue', align: 'right', title: 'ProShip revenue in the comparable prior window.', sortValue: (r) => r.prevRevenue, cell: (r) => currency(r.prevRevenue) },
          { key: 'prevShip', header: 'Prev Shipments', align: 'right', title: 'ProShip shipments in the comparable prior window.', sortValue: (r) => r.prevShipments, cell: (r) => integer(r.prevShipments) },
          { key: 'currRev', header: 'Curr Revenue', align: 'right', title: 'ProShip revenue in the selected window.', sortValue: (r) => r.revenue, cell: (r) => currency(r.revenue) },
          { key: 'currShip', header: 'Curr Shipments', align: 'right', title: 'ProShip shipments in the selected window.', sortValue: (r) => r.shipments, cell: (r) => integer(r.shipments) },
          { key: 'daysSince', header: 'Days Since Last Shipment', align: 'right', title: "Days since the brand's last positive shipment, vs the ProShip data-through date.", sortValue: (r) => (r.daysSinceShipment == null ? -1 : r.daysSinceShipment), cell: (r) => (r.daysSinceShipment == null ? '—' : integer(r.daysSinceShipment)) },
          { key: 'revChg', header: 'Rev Change %', align: 'right', title: 'Revenue % change vs the comparable prior window.', sortValue: (r) => r.revChangePct, cell: (r) => <GrowthPct pct={r.revChangePct} /> },
          { key: 'shipChg', header: 'Ship Change %', align: 'right', title: 'Shipment % change vs the comparable prior window.', sortValue: (r) => r.shipChangePct, cell: (r) => <GrowthPct pct={r.shipChangePct} /> },
          { key: 'risk', header: 'Risk Type', title: 'Churned = no shipments this window (after shipping before); Declining = revenue down vs prior.', sortValue: (r) => r.riskType, cell: (r) => <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: '9999px', border: `1px solid ${GX_BORDER}`, fontSize: 11, fontWeight: 600, color: r.riskType === 'Churned' ? GX_NEGATIVE : '#b45309' }}>{r.riskType}</Box> },
        ]}
      />
    </Box>
  )
}

function Stat({ label, value, accent, definition }) {
  return (
    <Tooltip title={definition || ''}>
      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: INK_400 }}>{label}</Typography>
        <Typography sx={{ mt: 0.25, fontSize: 18, fontWeight: 700, color: accent || GX_INK_900, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      </Box>
    </Tooltip>
  )
}

function PGroup({ title, children }) {
  return (
    <Box sx={{ border: `1px solid ${GX_BORDER}`, borderRadius: '12px', p: 1.75, bgcolor: '#fbfcfe' }}>
      <Typography sx={{ mb: 1, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: GX_COLORS.indigo }}>{title}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 1.25 }}>{children}</Box>
    </Box>
  )
}

function RiskCard({ label, count, description, color, open, onClick }) {
  return (
    <Box
      component="button" type="button" onClick={onClick}
      sx={{
        textAlign: 'left', cursor: 'pointer', font: 'inherit', bgcolor: '#fff', p: 2, borderRadius: '12px',
        border: `1px solid ${GX_BORDER}`, boxShadow: open ? `0 0 0 2px ${color}55` : 'none',
        '&:hover': { boxShadow: `0 1px 4px rgba(15,23,42,0.08)${open ? `, 0 0 0 2px ${color}55` : ''}` },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: INK_400 }}>{label}</Typography>
        <ExpandMoreIcon sx={{ fontSize: 18, color: INK_400, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </Box>
      <Typography sx={{ mt: 0.5, fontSize: 24, fontWeight: 700, color }}>{count}</Typography>
      <Typography sx={{ fontSize: 12, color: INK_400 }}>{description}</Typography>
    </Box>
  )
}

export default function SalesPocIndividualPage() {
  const { personId } = useParams()
  const [period, setPeriod] = useState('mtd')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [openRisk, setOpenRisk] = useState(null)
  const customReady = period !== 'custom' || Boolean(customFrom && customTo)

  const { data, isLoading, error } = useGetPocIndividualQuery(
    { personId, period, from: period === 'custom' ? customFrom : undefined, to: period === 'custom' ? customTo : undefined },
    { skip: !customReady },
  )

  const k = data?.kpis
  // HubSpot deep link for a deal record (0-3); portal id comes from the payload.
  const hubRec = (dealId) => (data?.hubspot ? `https://${data.hubspot.uiDomain}/contacts/${data.hubspot.portalId}/record/0-3/${dealId}` : undefined)
  const trend = data?.trend || []
  const chartEmpty = !isLoading && trend.every((t) => t.shipments === 0 && t.revenue === 0 && t.totalBrands === 0)
  const momUp = (k?.momGrowthPct || 0) >= 0
  const daysEmployed = data?.poc?.joiningDate
    ? Math.max(0, Math.floor((Date.now() - new Date(`${data.poc.joiningDate}T00:00:00`).getTime()) / 86400000))
    : null
  const loadingVal = isLoading ? '…' : '—'

  return (
    <Box sx={GX_PAGE_SX}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Link to="/growthos/pocs-analytics" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#4f46e5', fontSize: 12, fontWeight: 600, textDecoration: 'none', marginBottom: 8 }}>
            <ArrowBackIcon sx={{ fontSize: 14 }} /> All Sales POCs
          </Link>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>{data?.poc?.name || (isLoading ? 'Loading…' : 'Sales POC')}</Typography>
            {data?.poc?.team && (
              <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: '9999px', border: `1px solid ${GX_BORDER}`, fontSize: 11, fontWeight: 600, color: GX_INK_700 }}>
                {data.poc.team.replace(/_/g, ' ')}
              </Box>
            )}
          </Box>
          <Typography sx={{ fontSize: 13, color: GX_INK_500, mt: 0.5, maxWidth: 720 }}>
            Full-portfolio performance — every brand assigned to, shipped by, or closed-won by this POC (not just raw current rows).
          </Typography>
          {data?.poc && (
            <Box sx={{ mt: 1.5, display: 'inline-flex', flexDirection: 'column', border: `1px solid ${GX_BORDER}`, borderRadius: '8px', px: 1.5, py: 1 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: INK_400 }}>Joining date</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: GX_INK_900 }}>{data.poc.joiningDate ? shortDate(data.poc.joiningDate) : 'Not set'}</Typography>
              {daysEmployed != null
                ? <Typography sx={{ mt: 0.25, fontSize: 12, color: GX_INK_500 }}>{integer(daysEmployed)} days of employment</Typography>
                : <Typography sx={{ mt: 0.25, fontSize: 12, color: INK_400 }}>Set it in Data → Sales POCs</Typography>}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Select size="small" value={period} onChange={(e) => { setPeriod(e.target.value); setOpenRisk(null) }} sx={{ minWidth: 140, fontSize: 13, bgcolor: '#fff' }}>
            {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
          {period === 'custom' && (
            <>
              <TextField size="small" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              <Typography sx={{ color: GX_INK_500 }}>→</Typography>
              <TextField size="small" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </>
          )}
          {data?.window && (
            <GxPill color={GX_COLORS.indigo} icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}>
              Showing: {data.window.label} · {shortDate(data.window.from)} – {shortDate(data.window.to)}
            </GxPill>
          )}
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 2, p: 2, borderRadius: '16px', border: '1px solid #fecdd3', bgcolor: '#fff1f2', color: '#be123c', fontSize: 13 }}>
          {error?.data?.message || error?.message || `HTTP ${error?.status}`}
        </Box>
      )}
      {period === 'custom' && !customReady && (
        <Box sx={{ mb: 2, p: 2, borderRadius: '16px', border: '1px solid #fde68a', bgcolor: '#fffbeb', color: '#b45309', fontSize: 13 }}>
          Pick a start and end date to load the custom range.
        </Box>
      )}

      {/* KPI row (8) */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Kpi label="Revenue" icon={<CurrencyRupeeIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.indigo}
          value={k ? currency(k.revenue.value) : loadingVal} sub={k ? comparison(k.revenue, currency) : undefined}
          definition="ProShip revenue of the brands assigned to this POC — the same basis as the Sales POC leaderboard and the rank below. The portfolio tables further down are brand-centric views of the whole book." />
        <Kpi label="Shipments" icon={<Inventory2Icon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.teal}
          value={k ? integer(k.shipments.value) : loadingVal} sub={k ? comparison(k.shipments, integer) : undefined}
          definition="ProShip shipments of the POC's assigned brands in the window — same basis as the leaderboard." />
        <Kpi label="DRR Shipments" icon={<SpeedIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.teal}
          value={k ? integer(Math.round(k.drrShipments)) : loadingVal}
          sub={k ? (k.drrClippedToFirstShipment
            ? `shipments/day · ${k.drrDays} days since ${shortDate(k.drrFrom)}`
            : `shipments/day · ${k.drrDays || k.windowDays}-day window`) : undefined}
          definition="Daily Run Rate — shipments ÷ days. The clock starts at the portfolio's FIRST shipment or the window start, whichever is later, so a wide window like All time is not divided by years in which the POC had nothing to ship." />
        <Kpi label="Growth vs Prior" icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
          iconColor={k?.momGrowthPct == null ? GX_COLORS.indigo : momUp ? GX_COLORS.teal : GX_COLORS.pink}
          value={k ? (k.momGrowthPct == null ? '—' : `${momUp ? '↑' : '↓'} ${Math.abs(k.momGrowthPct).toFixed(1)}%`) : loadingVal}
          sub="Revenue vs comparable prior period"
          definition="Revenue vs the immediately preceding equal-length window — a month only when the period is MTD/Last Month." />
        <Kpi label="Active Brands" icon={<StorefrontIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.amber}
          value={k ? integer(k.activeBrands) : loadingVal} sub={k ? `of ${integer(k.portfolioBrands)} in portfolio` : undefined}
          definition="Assigned brands with ≥1 positive shipment in the window. Portfolio = assigned + closed-won brands (includes hidden-from-display brands; tables below list visible ones only)." />
        <Kpi label="POC Rank" icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.pink}
          value={k ? (k.pocRank == null ? '—' : `#${k.pocRank}`) : loadingVal} sub={k ? `of ${k.totalPocs} POCs by revenue` : undefined}
          definition="Rank on the Sales POC leaderboard by revenue — the same Revenue figure shown on this page." />
        <Kpi label="Win Rate" icon={<PercentIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.indigo}
          value={k ? percentage(k.winRate) : loadingVal} sub="Won ÷ decided deals"
          definition="Deals Won ÷ decided deals (Won + Lost), closed in window." />
        <Kpi label="Total Deals" icon={<TrackChangesIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.teal}
          value={k ? integer(k.totalDeals) : loadingVal} sub="HubSpot deals created in period" />
      </Box>

      {/* Two trend charts — trailing 12 months */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3, mb: 3 }}>
        <ChartCard
          title={<><TrendingUpIcon sx={{ fontSize: 16, color: C.revenue }} /><Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_900 }}>Shipments &amp; Revenue Monthly Trend</Typography><ScopeTag label="Trailing 12 months" title="Always the last 12 calendar months of ProShip data — independent of the period filter above." /></>}
          subtitle="Trailing 12 months across the portfolio — shipments (bars) and revenue (line)."
          empty={chartEmpty}
        >
          <ComposedChart data={trend} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <defs>
              <linearGradient id="pocShipBar" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.shipments} stopOpacity={0.95} /><stop offset="100%" stopColor={C.shipments} stopOpacity={0.55} /></linearGradient>
              <linearGradient id="pocRevArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.revenue} stopOpacity={0.22} /><stop offset="100%" stopColor={C.revenue} stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" vertical={false} />
            <XAxis dataKey="month" tickFormatter={monthLabel} tick={axisTick} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} minTickGap={12} />
            <YAxis yAxisId="left" tick={axisTick} tickLine={false} axisLine={false} width={44} />
            <YAxis yAxisId="right" orientation="right" tick={axisTick} tickLine={false} axisLine={false} width={52} tickFormatter={(v) => compactCurrency(v)} />
            <RTooltip content={<TrendTooltip rows={[{ key: 'shipments', label: 'Shipments', color: C.shipments, fmt: integer }, { key: 'revenue', label: 'Revenue', color: C.revenue, fmt: currency }]} />} cursor={{ fill: 'rgba(13,148,136,.06)' }} />
            <Legend verticalAlign="bottom" height={28} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="left" dataKey="shipments" name="Shipments" fill="url(#pocShipBar)" radius={[5, 5, 0, 0]} maxBarSize={38} />
            <Area yAxisId="right" dataKey="revenue" name="Revenue" stroke="none" fill="url(#pocRevArea)" legendType="none" tooltipType="none" />
            <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke={C.revenue} strokeWidth={2.5} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }} />
          </ComposedChart>
        </ChartCard>

        <ChartCard
          title={<><StorefrontIcon sx={{ fontSize: 16, color: C.totalBrands }} /><Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_900 }}>Brands Monthly Trend</Typography><ScopeTag label="Trailing 12 months" title="Always the last 12 calendar months of ProShip data — independent of the period filter above." /></>}
          subtitle="Trailing 12 months — active brands that month (bars) vs portfolio shipping by then (line)."
          empty={chartEmpty}
        >
          <ComposedChart data={trend} margin={{ top: 8, right: 12, left: 4, bottom: 4 }}>
            <defs>
              <linearGradient id="pocActiveBar" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.activeBrands} stopOpacity={0.95} /><stop offset="100%" stopColor={C.activeBrands} stopOpacity={0.55} /></linearGradient>
              <linearGradient id="pocTotalArea" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.totalBrands} stopOpacity={0.2} /><stop offset="100%" stopColor={C.totalBrands} stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" vertical={false} />
            <XAxis dataKey="month" tickFormatter={monthLabel} tick={axisTick} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} minTickGap={12} />
            <YAxis tick={axisTick} tickLine={false} axisLine={false} width={40} allowDecimals={false} />
            <RTooltip content={<TrendTooltip rows={[{ key: 'activeBrands', label: 'Active Brands', color: C.activeBrands, fmt: integer }, { key: 'totalBrands', label: 'Total Brands', color: C.totalBrands, fmt: integer }]} />} cursor={{ fill: 'rgba(6,182,212,.06)' }} />
            <Legend verticalAlign="bottom" height={28} iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="activeBrands" name="Active Brands" fill="url(#pocActiveBar)" radius={[5, 5, 0, 0]} maxBarSize={38} />
            <Area dataKey="totalBrands" name="Total Brands" stroke="none" fill="url(#pocTotalArea)" legendType="none" tooltipType="none" />
            <Line type="monotone" dataKey="totalBrands" name="Total Brands" stroke={C.totalBrands} strokeWidth={2.5} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }} />
          </ComposedChart>
        </ChartCard>
      </Box>

      {/* At-Risk Brands */}
      <Section
        icon={<WarningAmberIcon sx={{ fontSize: 18, color: GX_COLORS.amber }} />}
        title="At-Risk Brands" subtitle="Portfolio brands slipping this period · click a card to expand"
      >
        {data && !data.atRisk.available ? (
          <Box sx={{ p: 2, borderRadius: '12px', border: '1px solid #fde68a', bgcolor: '#fffbeb', color: '#b45309', fontSize: 13 }}>
            At-risk needs a defined current period vs a comparable prior period. &ldquo;All time&rdquo; has no comparable prior — pick MTD, Last Month, a range, or Custom.
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <RiskCard label="Churned Brands" count={data ? integer(data.atRisk.churnedCount) : '—'} description="Was active before, no shipment in 30d"
                color={GX_NEGATIVE} open={openRisk === 'churned'} onClick={() => setOpenRisk(openRisk === 'churned' ? null : 'churned')} />
              <RiskCard label="Declining Brands" count={data ? integer(data.atRisk.decliningCount) : '—'} description="Still shipping but revenue is down"
                color="#b45309" open={openRisk === 'declining'} onClick={() => setOpenRisk(openRisk === 'declining' ? null : 'declining')} />
            </Box>
            {openRisk && (
              <Box sx={{ mt: 1.5, border: `1px solid ${GX_BORDER}`, borderRadius: '12px' }}>
                <RiskTable rows={openRisk === 'churned' ? (data?.atRisk.churned || []) : (data?.atRisk.declining || [])} />
              </Box>
            )}
          </>
        )}
      </Section>

      {/* Active Pipeline view */}
      <Section
        icon={<TrackChangesIcon sx={{ fontSize: 18, color: GX_COLORS.indigo }} />}
        title="Active Pipeline view"
        right={data?.pipeline ? <ScopeTag label={`HubSpot: ${data.pipeline.timeframe}`} title="Deal counts use the HubSpot window; shipments/revenue achieved are ProShip actuals for this POC's closed-won brands." /> : null}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 1.5 }}>
          <PGroup title="Deals">
            <Stat label="Deals Created" value={data ? integer(data.pipeline.dealsCreated) : '—'} definition="Marketing-sourced HubSpot deals created in the window (all pipelines)." />
            <Stat label="WIP · New" value={data ? integer(data.pipeline.wipNew) : '—'} definition="Work In Progress — created in window, still open AND Hot/Warm. Cold/Hibernating/unclassified open deals are excluded." />
            <Stat label="WIP · Total" value={data ? integer(data.pipeline.wipTotal) : '—'} definition="Work In Progress — ALL open Hot/Warm deals, any creation date (live snapshot)." />
            <Stat label="Lost" value={data ? integer(data.pipeline.lost) : '—'} accent={GX_NEGATIVE} />
          </PGroup>
          <PGroup title="Customers">
            <Stat label="Customers · New" value={data ? integer(data.pipeline.customersNew) : '—'} />
            <Stat label="Customers · Total" value={data ? integer(data.pipeline.customersTotal) : '—'} accent={GX_POSITIVE} />
          </PGroup>
          <PGroup title="Shipments">
            <Stat label="Potential (monthly)" value={data ? integer(data.pipeline.shipmentsPotential) : '—'} />
            <Stat label="Achieved" value={data ? (data.pipeline.notLive ? 'Brand not live' : integer(data.pipeline.shipmentsAchieved)) : '—'} accent={data?.pipeline.notLive ? INK_400 : undefined} />
          </PGroup>
          <PGroup title="Revenue">
            <Stat label="Potential (MBR)" value={data ? currency(data.pipeline.revenuePotential) : '—'} />
            <Stat label="Achieved" value={data ? (data.pipeline.notLive ? 'Brand not live' : currency(data.pipeline.revenueAchieved)) : '—'} accent={data?.pipeline.notLive ? INK_400 : undefined} />
          </PGroup>
        </Box>
      </Section>

      {/* Top 5 Brands Contribution */}
      <Section
        icon={<EmojiEventsIcon sx={{ fontSize: 18, color: GX_COLORS.amber }} />}
        title="Top 5 Brands Contribution" subtitle="By current-period revenue · share of this POC's revenue"
      >
        {!(data?.top5?.brands || []).length ? (
          <Typography sx={{ py: 3, textAlign: 'center', fontSize: 13, color: INK_400 }}>No revenue in this period.</Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {data.top5.brands.map((b) => (
                <Box key={b.brandId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: GX_INK_900 }}>{b.brandName}</Typography>
                    <Typography sx={{ fontSize: 13, color: GX_INK_500, fontVariantNumeric: 'tabular-nums' }}>
                      {currency(b.revenue)} · <b>{percentage(b.sharePct)}</b>
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 0.5, height: 8, borderRadius: '9999px', overflow: 'hidden', bgcolor: '#eef2f7' }}>
                    <Box sx={{ height: '100%', borderRadius: '9999px', width: `${Math.min(100, b.sharePct || 0)}%`, background: 'linear-gradient(90deg,#6366f1,#06b6d4)' }} />
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${GX_BORDER}`, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, fontSize: 13 }}>
              <Typography sx={{ fontSize: 13, color: GX_INK_500 }}>Top 5 total</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{currency(data.top5.totalRevenue)} · {percentage(data.top5.sharePct)} of revenue</Typography>
            </Box>
          </>
        )}
      </Section>

      {/* Brand Portfolio */}
      <Section
        icon={<StorefrontIcon sx={{ fontSize: 18, color: GX_COLORS.indigo }} />}
        title="Brand Portfolio"
        subtitle={`${data ? `${data.portfolio.length} rows` : ''}${data?.portfolioAwaitingProship ? ` · ${data.portfolioAwaitingProship} awaiting ProShip` : ''} · sortable · TAT = closed-won → first shipment`}
      >
        <GxPagedTable variant="poc"
          rows={data?.portfolio || []}
          rowKey={(b) => b.rowId || b.brandId}
          emptyText="No brands in this POC's portfolio."
          columns={[
            {
              key: 'brand', header: 'Brand', sortValue: (b) => b.brandName,
              title: 'Brand name (ProShip / Data → Brands). Rows tagged "Awaiting ProShip" are closed-won deals with no ProShip brand yet — real wins with no actuals to show.',
              cell: (b) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography component="span" sx={{ fontWeight: 600, fontSize: 12.5, color: GX_INK_900 }}>{b.brandName}</Typography>
                  {b.notInProship && (
                    <Tooltip title={`Closed-won${b.pipelineLabel ? ` in ${b.pipelineLabel}` : ''} but not yet linked to a ProShip brand, so it has no shipments or revenue.`}>
                      <Box component="span" sx={{ px: 0.75, py: 0.25, borderRadius: '4px', fontSize: 10, fontWeight: 600, color: '#9a3412', bgcolor: '#ffedd5' }}>Awaiting ProShip</Box>
                    </Tooltip>
                  )}
                </Box>
              ),
            },
            {
              key: 'deal', header: 'Deal', cellSx: { whiteSpace: 'normal', minWidth: 200 }, sortValue: (b) => b.dealName || '',
              title: "This POC's deal for the brand — the won one where there is one, otherwise the most recent. A dash means the brand ships under this POC but has no HubSpot deal owned by them.",
              cell: (b) => (!b.dealName ? '—' : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography component="span" sx={{ fontWeight: 500, fontSize: 12.5 }}>{b.dealName}</Typography>
                  {b.dealId && hubRec(b.dealId) && (
                    <a href={hubRec(b.dealId)} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, color: '#4338ca', textDecoration: 'none' }}>
                      HubSpot <OpenInNewIcon sx={{ fontSize: 11 }} />
                    </a>
                  )}
                  {b.dealCount > 1 && (
                    <Tooltip title={`${b.dealCount} deals for this brand — showing the won/most recent one.`}>
                      <Box component="span" sx={{ fontSize: 10, color: INK_400 }}>+{b.dealCount - 1}</Box>
                    </Tooltip>
                  )}
                </Box>
              )),
            },
            { key: 'potential', header: 'Ship. Potential', align: 'right', title: 'Expected monthly shipments on the deal (HubSpot). Compare against actual Shipments to the right.', sortValue: (b) => (b.shipmentPotential == null ? -1 : b.shipmentPotential), cell: (b) => (b.shipmentPotential == null ? '—' : integer(b.shipmentPotential)) },
            { key: 'estMbr', header: 'Est. MBR', align: 'right', title: 'Estimated monthly business revenue on the deal (₹, HubSpot). Compare against actual Revenue to the right.', sortValue: (b) => (b.estimatedMbr == null ? -1 : b.estimatedMbr), cell: (b) => (b.estimatedMbr == null ? '—' : currency(b.estimatedMbr)) },
            { key: 'dealCreate', header: 'Deal Create Date', title: "Earliest HubSpot deal create date for the brand (this POC's deals).", sortValue: (b) => b.dealCreateDate || '', cell: (b) => shortDate(b.dealCreateDate) },
            { key: 'closed', header: 'Closed Date', title: 'Deal close date (HubSpot).', sortValue: (b) => b.closedDate || '', cell: (b) => shortDate(b.closedDate) },
            { key: 'firstShip', header: 'First Shipment Date', title: "The brand's first-ever positive ProShip shipment date.", sortValue: (b) => b.firstShipmentDate || '', cell: (b) => shortDate(b.firstShipmentDate) },
            { key: 'tat', header: 'Closed→1st Ship TAT', align: 'right', sortValue: (b) => (b.tatDays == null ? -1 : b.tatDays), cell: (b) => (b.tatDays == null ? '—' : `${integer(b.tatDays)}d`) },
            { key: 'ship', header: 'Shipments', align: 'right', title: 'ProShip shipments in the selected window. A dash means the brand is not in ProShip yet — different from a zero, which would mean it shipped nothing.', sortValue: (b) => (b.shipments == null ? -1 : b.shipments), cell: (b) => (b.shipments == null ? '—' : integer(b.shipments)) },
            { key: 'rev', header: 'Revenue', align: 'right', title: 'ProShip revenue (₹) in the selected window. A dash means not in ProShip yet.', sortValue: (b) => (b.revenue == null ? -1 : b.revenue), cell: (b) => (b.revenue == null ? '—' : currency(b.revenue)) },
            { key: 'revChg', header: '% Revenue Change', align: 'right', title: 'Revenue % change vs the comparable prior window.', sortValue: (b) => b.revChangePct, cell: (b) => <GrowthPct pct={b.revChangePct} /> },
          ]}
        />
      </Section>
    </Box>
  )
}
